import { DateTime } from 'luxon';
import { Fragment } from 'react';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { SortDirectionMenu } from '@/pages/Schedule/pages/ScheduleMessages/components/SortDirectionMenu';
import { MessageStatusType, SortDirection } from '@/types/gql.generated';
import { Box, Divider, Flex, Heading, Spinner } from '@/ui';
import { SectionHeader } from '../ScheduleSettings/SectionHeader';
import type { ExperienceEventMessage } from './hooks/useExperienceEventMessages';
import { useExperienceEventMessages } from './hooks/useExperienceEventMessages';
import { MessageRow } from './MessageRow';
import { MessagesEmptyState } from './MessagesEmptyState';

export type MessagesView = 'all' | 'scheduled' | 'sent';

const VIEW_COPY: Record<MessagesView, { title: string; description: string }> =
  {
    all: {
      title: 'All messages',
      description: 'Every text reminder scheduled for events on this planner',
    },
    scheduled: {
      title: 'Scheduled',
      description: 'Text reminders waiting to be sent',
    },
    sent: {
      title: 'Sent',
      description: 'Text reminders that have already been sent',
    },
  };

const matchesView = (view: MessagesView, message: ExperienceEventMessage) => {
  if (view === 'scheduled') {
    return message.status === MessageStatusType.Scheduled;
  }
  if (view === 'sent') {
    return message.status === MessageStatusType.Sent;
  }
  return true;
};

type MessageEntry = {
  message: ExperienceEventMessage;
  sendAt: DateTime;
};

type MessageGroup = {
  key: string;
  label: string;
  messages: MessageEntry[];
};

// Groups messages by their send date — shown in the event's time zone when it
// has one — ordered by the selected sort direction.
const groupBySendDate = (
  messages: ExperienceEventMessage[],
  displayTimezone: string,
  sortDirection: SortDirection
): MessageGroup[] => {
  const sorted = messages
    .map((message) => ({
      message,
      sendAt: DateTime.fromISO(message.sendAt).setZone(
        message.event.start.timeZone ?? displayTimezone
      ),
    }))
    .sort((a, b) =>
      sortDirection === SortDirection.Asc
        ? a.sendAt.toMillis() - b.sendAt.toMillis()
        : b.sendAt.toMillis() - a.sendAt.toMillis()
    );

  const groups = new Map<string, MessageGroup>();
  sorted.forEach((entry) => {
    const key = entry.sendAt.toISODate() ?? '';
    const group = groups.get(key) ?? {
      key,
      label: entry.sendAt.toFormat('MMMM d, yyyy'),
      messages: [],
    };
    group.messages.push(entry);
    groups.set(key, group);
  });

  return [...groups.values()];
};

type Props = {
  view: MessagesView;
  sortDirection: SortDirection;
  onSortDirectionChange: (direction: SortDirection) => void;
};

export const MessagesSection = ({
  view,
  sortDirection,
  onSortDirectionChange,
}: Props) => {
  const { displayTimezone } = usePageExperienceContext();
  const { messages, isLoading } = useExperienceEventMessages();

  const visible = messages.filter((message) => matchesView(view, message));
  const groups = groupBySendDate(visible, displayTimezone, sortDirection);

  return (
    <Box>
      <Flex align="flex-start" gap={4} justify="space-between" maxW="860px">
        <SectionHeader
          description={VIEW_COPY[view].description}
          title={VIEW_COPY[view].title}
        />
        {!isLoading && visible.length > 0 && (
          <SortDirectionMenu
            selected={sortDirection}
            onChange={onSortDirectionChange}
          />
        )}
      </Flex>

      {isLoading ? (
        <Flex justify="center" pt={10}>
          <Spinner color="magicPurple.500" />
        </Flex>
      ) : groups.length === 0 ? (
        <MessagesEmptyState />
      ) : (
        <Box maxW="860px">
          {groups.map((group, index) => (
            <Fragment key={group.key}>
              <Box>
                <Heading mb="6" size="md">
                  {group.label}
                </Heading>
                {group.messages.map(({ message, sendAt }, messageIndex) => (
                  <MessageRow
                    displayTimezone={displayTimezone}
                    key={message.id}
                    mb={messageIndex === group.messages.length - 1 ? 0 : 10}
                    message={message}
                    sendAt={sendAt}
                    showStatus={view === 'all'}
                  />
                ))}
              </Box>
              {index < groups.length - 1 && <Divider my="10" />}
            </Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};
